import { Radio } from 'antd';
import React, { FC, ComponentProps, ReactNode } from 'react';
import styled, { css } from 'styled-components';
import { ifProp, prop } from 'styled-tools';
import { theme } from 'components/styles/theme';

type SwitcherProps = Omit<
  ComponentProps<typeof Radio.Group>,
  'size' | 'buttonStyle'
> & {
  size?: keyof typeof theme.heights;
  solid?: boolean;
  activeColor?: string;
  hoverBgColor?: string;
};

type ButtonProps = ComponentProps<typeof Radio.Button> & {
  icon?: ReactNode;
};

const Icon = styled.div<{ iconWithChildren? }>`
  display: inline-flex;
  align-items: center;
`;
const Wrapper = styled.span<{ hasIconOnly: boolean }>`
  display: inline-flex;
  align-items: center;
  ${ifProp(
    'hasIconOnly',
    css`
      ${Icon} {
        margin-right: var(--spacing-03);
      }
    `,
  )};
`;
const ButtonInternal: FC<ButtonProps> = ({ icon, children, ...props }) => (
  <Radio.Button {...props} key={props.key as React.Key | undefined}>
    <Wrapper hasIconOnly={!!children}>
      {icon == null ? null : <Icon>{icon}</Icon>}
      {children}
    </Wrapper>
  </Radio.Button>
);

const Button = styled(ButtonInternal)``;

const SwitcherInternal: FC<SwitcherProps> = ({ size, solid, ...props }) => (
  <Radio.Group
    buttonStyle={solid ? 'solid' : 'outline'}
    {...props}
    key={props.key as React.Key | undefined}
  />
);

const SwitcherComponent = styled(SwitcherInternal)`
  display: flex;

  ${Button} {
    padding: 0 var(--layout-01);
    height: ${({ size = 'medium', theme: { heights } }) => heights[size]};
    line-height: ${({ size = 'medium', theme: { heights, pixels } }) =>
      `${pixels(heights[size]) - 2}px`};
    display: flex;
    border-radius: 0;
    .ant-radio-button + span {
      display: flex;
    }

    &:hover {
      color: ${prop('activeColor', 'var(--black-100)')};
      background-color: ${prop('hoverBgColor', 'var(--gray-10)')};
    }

    &.ant-radio-button-wrapper-checked:not(.ant-radio-button-wrapper-disabled) {
      color: white;
      background-color: ${prop('activeColor', 'var(--black-100)')};
      border-color: ${prop('activeColor', 'var(--black-100)')};
    }
    &.ant-radio-button-wrapper-checked:not(.ant-radio-button-wrapper-disabled)::before {
      background-color: ${prop('activeColor', 'var(--black-100)')};
    }
  }
`;

type Switcher = typeof SwitcherComponent & {
  Button: typeof Button;
};

const Switcher: Switcher = SwitcherComponent as any;

Switcher.Button = Button;

export { Switcher };
